"use client";
import React, { ReactElement, Suspense, lazy, useEffect } from "react";
import { usePathname } from "next/navigation";
import { Toaster } from "react-hot-toast";
import { useHeader } from "@/context/HeaderContext";
import LogoOnlyHeader from "@/components/Atoms/Header/LogoOnlyHeader";
import LogoOnlyHeaderMobile from "@/components/Atoms/Header/LogoOnlyHeaderMobile";

function ScrollToTop() {
  const pathname = usePathname();

  useEffect(() => {
    // Disable browser's automatic scroll restoration so we control it
    if (typeof window !== "undefined" && "scrollRestoration" in history) {
      history.scrollRestoration = "manual";
    }
    // Handle popstate (back/forward browser navigation)
    const handlePop = () => {
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    };
    window.addEventListener("popstate", handlePop);
    return () => window.removeEventListener("popstate", handlePop);
  }, []);

  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, [pathname]);

  return null;
}

// Lazy load components
const EmbarcaLog = lazy(() => import("@/components/Atoms/Header/EmbarcaLog"));
const HeaderMobileEmbarcaLog = lazy(
  () => import("@/components/Atoms/Header/default")
);
const ContainerDesktop = lazy(
  () => import("@/components/Atoms/Container/desktop")
);
const ContainerMobile = lazy(
  () => import("@/components/Atoms/Container/mobile")
);
const SignModal = lazy(() => import("@/components/Molecules/Sign"));

export default function EncomendasLayout({
  children,
}: {
  children: ReactElement;
}) {
  const pathname = usePathname();
  const { hasCustomHeader } = useHeader();

  if (pathname === "/encomendas/web-view") {
    return <>{children}</>;
  }

  return (
    <>
      <ScrollToTop />
      {!hasCustomHeader && (
        <>
          {/* Desktop header — hidden on mobile via CSS only (no JS gate) */}
          <div className="sticky top-0 z-50 hidden md:block">
            <Suspense fallback={<div className="h-16" />}>
              <EmbarcaLog />
            </Suspense>
          </div>

          {/* Mobile header — hidden on desktop via CSS only (no JS gate) */}
          <div className="sticky top-0 z-50 block md:hidden">
            <Suspense fallback={<div className="h-16" />}>
              <HeaderMobileEmbarcaLog />
            </Suspense>
          </div>
        </>
      )}

      <div className="max-w-[1381px] mx-auto w-full">{children}</div>

      <Suspense fallback={null}>
        <SignModal />
      </Suspense>
      <Toaster
        toastOptions={{
          style: {
            fontFamily: "inherit",
          },
        }}
      />
    </>
  );
}
